<template>
  <div class="admin-dashboard">
    <div class="container-fluid mt-4">
      <div v-if="!authenticated" class="row justify-content-center">
        <div class="col-md-4">
          <div class="card">
            <div class="card-header bg-dark text-white">
              <h4 class="mb-0">Admin Login</h4>
            </div>
            <div class="card-body">
              <div class="form-group">
                <label>Admin Token</label>
                <input 
                  type="password" 
                  class="form-control" 
                  v-model="adminToken"
                  @keyup.enter="login"
                  placeholder="Enter admin token"
                >
              </div>
              <button class="btn btn-primary btn-block mt-3" @click="login" :disabled="loading">
                {{ loading ? 'Loading...' : 'Login' }}
              </button>
              <p v-if="error" class="text-danger mt-2">{{ error }}</p>
            </div>
          </div>
        </div>
      </div>

      <div v-else>
        <div class="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 class="mb-1">Admin Dashboard</h2>
            <small v-if="metrics && metrics.server_time" class="text-muted">
              Server time: {{ formatDate(metrics.server_time) }} (UTC)
            </small>
          </div>
          <div>
            <button class="btn btn-warning btn-sm mr-2" @click="clearStaleJobs">
              Clear Stale Jobs
            </button>
            <button class="btn btn-outline-secondary btn-sm" @click="refreshMetrics">
              Refresh
            </button>
          </div>
        </div>

        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border" role="status"></div>
          <p class="mt-2">Loading metrics...</p>
        </div>

        <div v-else-if="metrics">
          <div class="row mb-4">
            <div class="col-6 col-sm-4 col-md-2 mb-3">
              <div class="card bg-primary text-white h-100">
                <div class="card-body text-center py-3">
                  <h3 class="mb-1">{{ metrics.overview.total_jobs }}</h3>
                  <small>Total Jobs</small>
                </div>
              </div>
            </div>
            <div class="col-6 col-sm-4 col-md-2 mb-3">
              <div class="card bg-success text-white h-100">
                <div class="card-body text-center py-3">
                  <h3 class="mb-1">{{ metrics.overview.completed }}</h3>
                  <small>Completed</small>
                </div>
              </div>
            </div>
            <div class="col-6 col-sm-4 col-md-2 mb-3">
              <div class="card bg-danger text-white h-100">
                <div class="card-body text-center py-3">
                  <h3 class="mb-1">{{ metrics.overview.failed }}</h3>
                  <small>Failed</small>
                </div>
              </div>
            </div>
            <div class="col-6 col-sm-4 col-md-2 mb-3">
              <div class="card bg-warning text-dark h-100">
                <div class="card-body text-center py-3">
                  <h3 class="mb-1">{{ metrics.overview.pending }}</h3>
                  <small>Pending</small>
                </div>
              </div>
            </div>
            <div class="col-6 col-sm-4 col-md-2 mb-3">
              <div class="card bg-info text-white h-100">
                <div class="card-body text-center py-3">
                  <h3 class="mb-1">{{ metrics.overview.processing }}</h3>
                  <small>Processing</small>
                </div>
              </div>
            </div>
            <div class="col-6 col-sm-4 col-md-2 mb-3">
              <div class="card bg-secondary text-white h-100">
                <div class="card-body text-center py-3">
                  <h3 class="mb-1">{{ metrics.overview.success_rate }}%</h3>
                  <small>Success Rate</small>
                </div>
              </div>
            </div>
          </div>

          <div class="row mb-4">
            <div class="col-12 col-md-6 col-lg-3 mb-3">
              <div class="card h-100">
                <div class="card-header">Activity</div>
                <div class="card-body">
                  <div class="table-responsive">
                    <table class="table table-sm mb-0">
                      <tr><td>Jobs (24h)</td><td><strong>{{ metrics.activity.jobs_24h }}</strong></td></tr>
                      <tr><td>Jobs (7d)</td><td><strong>{{ metrics.activity.jobs_7d }}</strong></td></tr>
                      <tr><td>Jobs (30d)</td><td><strong>{{ metrics.activity.jobs_30d }}</strong></td></tr>
                      <tr><td>Unique Users (24h)</td><td><strong>{{ metrics.activity.unique_users_24h }}</strong></td></tr>
                      <tr><td>Unique Users (7d)</td><td><strong>{{ metrics.activity.unique_users_7d }}</strong></td></tr>
                      <tr><td>Unique Users (Total)</td><td><strong>{{ metrics.activity.unique_users_total }}</strong></td></tr>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-12 col-md-6 col-lg-3 mb-3">
              <div class="card h-100">
                <div class="card-header bg-info text-white">Usage Stats</div>
                <div class="card-body">
                  <div class="table-responsive">
                    <table class="table table-sm mb-0">
                      <tr><td>Helper Clicks (24h)</td><td><strong>{{ usageStats.helper_clicks_24h }}</strong></td></tr>
                      <tr><td>Helper Clicks (Total)</td><td><strong>{{ usageStats.helper_clicks_total }}</strong></td></tr>
                      <tr><td>AI Calls (24h)</td><td><strong>{{ usageStats.ai_calls_24h }}</strong></td></tr>
                      <tr><td>AI Calls (Total)</td><td><strong>{{ usageStats.ai_calls_total }}</strong></td></tr>
                      <tr><td>Downloads (24h)</td><td><strong>{{ usageStats.downloads_24h }}</strong></td></tr>
                      <tr><td>Downloads (Total)</td><td><strong>{{ usageStats.downloads_total }}</strong></td></tr>
                      <tr><td>Moderation Rejects (24h)</td><td><strong class="text-danger">{{ usageStats.moderation_rejects_24h || 0 }}</strong></td></tr>
                      <tr><td>Moderation Rejects (Total)</td><td><strong class="text-danger">{{ usageStats.moderation_rejects_total || 0 }}</strong></td></tr>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-12 col-md-6 col-lg-3 mb-3">
              <div class="card h-100">
                <div class="card-header bg-success text-white">Top Video Creators</div>
                <div class="card-body" style="max-height: 250px; overflow-y: auto;">
                  <div class="table-responsive">
                    <table class="table table-sm mb-0">
                      <thead>
                        <tr><th>IP</th><th>Jobs</th></tr>
                      </thead>
                      <tbody>
                        <tr v-for="user in topUsers" :key="user.ip">
                          <td><code class="small text-truncate d-inline-block" style="max-width: 120px;">{{ user.ip }}</code></td>
                          <td>{{ user.jobs }}</td>
                        </tr>
                        <tr v-if="!topUsers.length">
                          <td colspan="2" class="text-muted">No data yet</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-12 col-md-6 col-lg-3 mb-3">
              <div class="card h-100">
                <div class="card-header bg-warning text-dark">Top Helper Users</div>
                <div class="card-body" style="max-height: 250px; overflow-y: auto;">
                  <div class="table-responsive">
                    <table class="table table-sm mb-0">
                      <thead>
                        <tr><th>IP</th><th>Clicks</th></tr>
                      </thead>
                      <tbody>
                        <tr v-for="user in topHelperUsers" :key="user.ip">
                          <td><code class="small text-truncate d-inline-block" style="max-width: 120px;">{{ user.ip }}</code></td>
                          <td>{{ user.clicks }}</td>
                        </tr>
                        <tr v-if="!topHelperUsers.length">
                          <td colspan="2" class="text-muted">No data yet</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

          </div>

          <div class="row mb-4">
            <div class="col-12 col-md-6 mb-3">
              <div class="card">
                <div class="card-header">Top Visitors (All Activity)</div>
                <div class="card-body" style="max-height: 250px; overflow-y: auto;">
                  <div class="table-responsive">
                    <table class="table table-sm mb-0">
                      <thead>
                        <tr><th>IP</th><th>Events</th></tr>
                      </thead>
                      <tbody>
                        <tr v-for="user in topVisitors" :key="user.ip">
                          <td><code class="small text-truncate d-inline-block" style="max-width: 150px;">{{ user.ip }}</code></td>
                          <td>{{ user.events }}</td>
                        </tr>
                        <tr v-if="!topVisitors.length">
                          <td colspan="2" class="text-muted">No data yet</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="row mb-4">
            <div class="col-12">
              <div class="card">
                <div class="card-header bg-dark text-white d-flex justify-content-between align-items-center">
                  <span>Site Announcements</span>
                  <button class="btn btn-sm btn-light" @click="showAnnouncementForm = !showAnnouncementForm">
                    {{ showAnnouncementForm ? 'Cancel' : '+ New' }}
                  </button>
                </div>
                <div class="card-body">
                  <div v-if="showAnnouncementForm" class="mb-3 p-3 bg-light rounded">
                    <div class="form-group mb-2">
                      <label class="small">Message</label>
                      <textarea 
                        v-model="newAnnouncement.message" 
                        class="form-control form-control-sm" 
                        rows="2" 
                        placeholder="Enter announcement message..."
                      ></textarea>
                    </div>
                    <div class="d-flex align-items-center">
                      <div class="form-group mb-0 mr-3">
                        <label class="small">Type</label>
                        <select v-model="newAnnouncement.type" class="form-control form-control-sm">
                          <option value="info">Info (Blue)</option>
                          <option value="warning">Warning (Yellow)</option>
                          <option value="success">Success (Green)</option>
                          <option value="danger">Alert (Red)</option>
                        </select>
                      </div>
                      <button 
                        class="btn btn-primary btn-sm mt-3" 
                        @click="createAnnouncement" 
                        :disabled="!newAnnouncement.message.trim()"
                      >
                        Create Announcement
                      </button>
                    </div>
                  </div>
                  
                  <div v-if="announcements.length === 0" class="text-muted text-center py-3">
                    No announcements yet
                  </div>
                  
                  <div v-for="ann in announcements" :key="ann.id" class="d-flex align-items-center mb-2 p-2 rounded" :class="'border-left border-' + ann.type" style="border-left-width: 4px !important; background: #f8f9fa;">
                    <div class="flex-grow-1">
                      <span :class="{'text-muted': !ann.is_active}">{{ ann.message }}</span>
                      <small class="text-muted ml-2">({{ ann.type }})</small>
                      <span v-if="!ann.is_active" class="badge badge-secondary ml-2">Inactive</span>
                    </div>
                    <div class="btn-group btn-group-sm">
                      <button 
                        class="btn btn-outline-secondary py-0 px-2" 
                        @click="toggleAnnouncement(ann)"
                        :title="ann.is_active ? 'Deactivate' : 'Activate'"
                      >
                        {{ ann.is_active ? 'Hide' : 'Show' }}
                      </button>
                      <button 
                        class="btn btn-outline-danger py-0 px-2" 
                        @click="deleteAnnouncement(ann.id)"
                        title="Delete"
                      >
                        X
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="row mb-4">
            <div class="col-12 col-lg-6 mb-3">
              <div class="card h-100">
                <div class="card-header bg-dark text-white d-flex justify-content-between align-items-center">
                  <span>IP Blocklist</span>
                  <span class="badge badge-light">{{ blockedIps.length }}</span>
                </div>
                <div class="card-body">
                  <div class="input-group input-group-sm mb-3">
                    <input 
                      type="text" 
                      class="form-control" 
                      v-model="newBlockedIp" 
                      placeholder="Enter IP address to block"
                      @keyup.enter="addBlockedIp"
                    >
                    <div class="input-group-append">
                      <button class="btn btn-danger" @click="addBlockedIp" :disabled="!newBlockedIp.trim()">
                        Block
                      </button>
                    </div>
                  </div>
                  <div v-if="blockedIps.length === 0" class="text-muted text-center py-2">
                    No blocked IPs
                  </div>
                  <div v-for="ip in blockedIps" :key="ip" class="d-flex justify-content-between align-items-center mb-1 p-2 bg-light rounded">
                    <code class="small">{{ ip }}</code>
                    <button class="btn btn-sm btn-outline-success py-0 px-2" @click="removeBlockedIp(ip)" title="Unblock">
                      Unblock
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-12 col-lg-6 mb-3">
              <div class="card h-100">
                <div class="card-header bg-danger text-white d-flex justify-content-between align-items-center">
                  <span>Content Moderation Rejections</span>
                  <span class="badge badge-light">{{ usageStats.moderation_rejects_total || 0 }} total</span>
                </div>
                <div class="card-body p-0" style="max-height: 300px; overflow-y: auto;">
                  <div v-if="!recentModeration.length" class="text-muted text-center py-3">
                    No moderation rejections
                  </div>
                  <table v-else class="table table-sm mb-0">
                    <thead>
                      <tr>
                        <th>IP</th>
                        <th>Type</th>
                        <th>Reason</th>
                        <th class="d-none d-sm-table-cell">Time</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(evt, idx) in recentModeration" :key="idx">
                        <td><code class="small ip-cell">{{ evt.ip }}</code></td>
                        <td><span class="badge badge-warning">{{ evt.file_type }}</span></td>
                        <td><small class="text-danger" style="word-break: break-word;">{{ evt.reason }}</small></td>
                        <td class="d-none d-sm-table-cell"><small>{{ formatDate(evt.created_at) }}</small></td>
                        <td>
                          <button 
                            class="btn btn-sm btn-outline-danger py-0 px-1" 
                            @click="blockIpFromModeration(evt.ip)" 
                            :disabled="blockedIps.includes(evt.ip)"
                            :title="blockedIps.includes(evt.ip) ? 'Already blocked' : 'Block this IP'"
                          >
                            {{ blockedIps.includes(evt.ip) ? 'Blocked' : 'Block' }}
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <div class="row mb-4">
            <div class="col-12">
              <div class="card">
                <div class="card-header bg-primary text-white">Recent Jobs</div>
                <div class="card-body p-0 p-md-3">
                  <div class="table-responsive">
                    <table class="table table-sm mb-0">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Status</th>
                          <th class="d-none d-md-table-cell">Surah</th>
                          <th>IP</th>
                          <th class="d-none d-md-table-cell">Duration</th>
                          <th class="d-none d-sm-table-cell">Time</th>
                          <th class="d-none d-lg-table-cell">Elapsed</th>
                          <th>Download</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="job in metrics.recent_jobs" :key="job.id">
                          <td><code class="small" style="word-break: break-all;">{{ job.id }}</code></td>
                          <td>
                            <span :class="statusClass(job.status)" class="small">{{ job.status }}</span>
                            <span v-if="job.status === 'processing' && job.progress" class="small text-muted ml-1">({{ job.progress }}%)</span>
                          </td>
                          <td class="d-none d-md-table-cell">{{ job.surah || '-' }}</td>
                          <td><code class="small ip-cell">{{ job.ip || '-' }}</code></td>
                          <td class="d-none d-md-table-cell"><small>{{ formatDuration(job.duration) }}</small></td>
                          <td class="d-none d-sm-table-cell"><small>{{ formatDate(job.created_at) }}</small></td>
                          <td class="d-none d-lg-table-cell">
                            <small v-if="job.elapsed_seconds" :class="{'text-danger': job.elapsed_seconds > 2700, 'text-warning': job.elapsed_seconds > 900}">
                              {{ formatElapsed(job.elapsed_seconds) }}
                            </small>
                            <small v-else class="text-muted">-</small>
                          </td>
                          <td>
                            <button v-if="job.has_output && job.status === 'completed'" class="btn btn-sm btn-outline-success py-0 px-1" @click="downloadVideo(job.id)" title="Download video">
                              <i class="fa fa-download"></i>
                            </button>
                            <button v-else-if="job.status === 'pending' || job.status === 'processing'" 
                                    class="btn btn-sm btn-outline-danger py-0 px-1" 
                                    @click="cancelJob(job.id)" 
                                    :disabled="cancellingJob === job.id"
                                    title="Cancel job">
                              <i :class="cancellingJob === job.id ? 'fa fa-spinner fa-spin' : 'fa fa-times'"></i>
                            </button>
                            <span v-else class="text-muted small">-</span>
                          </td>
                        </tr>
                        <tr v-if="!metrics.recent_jobs.length">
                          <td colspan="8" class="text-muted">No jobs yet</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-12">
              <div class="card">
                <div class="card-header bg-danger text-white">Recent Failures</div>
                <div class="card-body p-0 p-md-3">
                  <div class="table-responsive">
                    <table class="table table-sm mb-0">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th class="d-none d-md-table-cell">Surah</th>
                          <th>IP</th>
                          <th class="d-none d-md-table-cell">Duration</th>
                          <th>Error</th>
                          <th class="d-none d-sm-table-cell">Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="job in metrics.recent_failures" :key="job.id">
                          <td><code class="small" style="word-break: break-all;">{{ job.id }}</code></td>
                          <td class="d-none d-md-table-cell">{{ job.surah || '-' }}</td>
                          <td><code class="small ip-cell">{{ job.ip || '-' }}</code></td>
                          <td class="d-none d-md-table-cell"><small>{{ formatDuration(job.duration) }}</small></td>
                          <td><small class="text-danger" style="word-break: break-word;">{{ job.error || 'Unknown error' }}</small></td>
                          <td class="d-none d-sm-table-cell"><small>{{ formatDate(job.created_at) }}</small></td>
                        </tr>
                        <tr v-if="!metrics.recent_failures.length">
                          <td colspan="6" class="text-muted">No failures</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- ══════════════════════════════════════════════════════
               AUDIO PROCESSING MODULE
               ══════════════════════════════════════════════════════ -->
          <div class="row mb-2 mt-4">
            <div class="col-12">
              <h5 class="text-muted font-weight-bold text-uppercase small mb-0">
                <i class="fa fa-headphones mr-1"></i> Audio Processing Module
              </h5>
              <hr class="mt-2 mb-3">
            </div>
          </div>

          <!-- Audio Overview Cards -->
          <div class="row mb-3">
            <div class="col-6 col-sm-3 mb-3">
              <div class="card bg-primary text-white h-100">
                <div class="card-body text-center py-3">
                  <h3 class="mb-1">{{ audioOverview.total }}</h3>
                  <small>Total Audio Jobs</small>
                </div>
              </div>
            </div>
            <div class="col-6 col-sm-3 mb-3">
              <div class="card bg-success text-white h-100">
                <div class="card-body text-center py-3">
                  <h3 class="mb-1">{{ audioOverview.completed }}</h3>
                  <small>Completed</small>
                </div>
              </div>
            </div>
            <div class="col-6 col-sm-3 mb-3">
              <div class="card bg-danger text-white h-100">
                <div class="card-body text-center py-3">
                  <h3 class="mb-1">{{ audioOverview.failed }}</h3>
                  <small>Failed</small>
                </div>
              </div>
            </div>
            <div class="col-6 col-sm-3 mb-3">
              <div class="card bg-secondary text-white h-100">
                <div class="card-body text-center py-3">
                  <h3 class="mb-1">{{ audioOverview.success_rate }}%</h3>
                  <small>Success Rate</small>
                </div>
              </div>
            </div>
          </div>

          <!-- Audio Stats + Top Users -->
          <div class="row mb-4">
            <div class="col-12 col-md-6 col-lg-4 mb-3">
              <div class="card h-100">
                <div class="card-header bg-info text-white">Audio Activity</div>
                <div class="card-body">
                  <div class="table-responsive">
                    <table class="table table-sm mb-0">
                      <tr><td>Jobs (24h)</td><td><strong>{{ audioActivity.jobs_24h }}</strong></td></tr>
                      <tr><td>Jobs (7d)</td><td><strong>{{ audioActivity.jobs_7d }}</strong></td></tr>
                      <tr><td>Jobs (30d)</td><td><strong>{{ audioActivity.jobs_30d }}</strong></td></tr>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-12 col-md-6 col-lg-4 mb-3">
              <div class="card h-100">
                <div class="card-header bg-info text-white">Audio Usage Stats</div>
                <div class="card-body">
                  <div class="table-responsive">
                    <table class="table table-sm mb-0">
                      <tr><td>Downloads (24h)</td><td><strong>{{ audioUsage.downloads_24h }}</strong></td></tr>
                      <tr><td>Downloads (Total)</td><td><strong>{{ audioUsage.downloads_total }}</strong></td></tr>
                      <tr><td>Previews (24h)</td><td><strong>{{ audioUsage.previews_24h }}</strong></td></tr>
                      <tr><td>Previews (Total)</td><td><strong>{{ audioUsage.previews_total }}</strong></td></tr>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-12 col-md-6 col-lg-4 mb-3">
              <div class="card h-100">
                <div class="card-header bg-success text-white">Top Audio Users</div>
                <div class="card-body" style="max-height: 200px; overflow-y: auto;">
                  <div class="table-responsive">
                    <table class="table table-sm mb-0">
                      <thead><tr><th>IP</th><th>Jobs</th></tr></thead>
                      <tbody>
                        <tr v-for="(u, i) in topAudioUsers" :key="i">
                          <td><code class="small ip-cell">{{ u.ip }}</code></td>
                          <td>{{ u.jobs }}</td>
                        </tr>
                        <tr v-if="!topAudioUsers.length">
                          <td colspan="2" class="text-muted">No data yet</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Recent Audio Jobs -->
          <div class="row mb-4">
            <div class="col-12">
              <div class="card">
                <div class="card-header bg-primary text-white">Recent Audio Jobs (last 10)</div>
                <div class="card-body p-0 p-md-3">
                  <div class="table-responsive">
                    <table class="table table-sm mb-0">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Status</th>
                          <th>IP</th>
                          <th class="d-none d-md-table-cell">Profile</th>
                          <th class="d-none d-md-table-cell">Format</th>
                          <th class="d-none d-sm-table-cell">Size</th>
                          <th class="d-none d-lg-table-cell">Proc. Time</th>
                          <th class="d-none d-sm-table-cell">Time</th>
                          <th>Download</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="job in recentAudioJobs" :key="job.id">
                          <td><code class="small">{{ job.id }}</code></td>
                          <td><span :class="statusClass(job.status)" class="small">{{ job.status }}</span></td>
                          <td><code class="small ip-cell">{{ job.ip }}</code></td>
                          <td class="d-none d-md-table-cell"><small>{{ job.profile }}</small></td>
                          <td class="d-none d-md-table-cell"><small>{{ job.format }}</small></td>
                          <td class="d-none d-sm-table-cell">
                            <small>{{ job.size_mb ? job.size_mb.toFixed(1) + ' MB' : '-' }}</small>
                          </td>
                          <td class="d-none d-lg-table-cell">
                            <small>{{ job.processing_seconds ? job.processing_seconds.toFixed(1) + 's' : '-' }}</small>
                          </td>
                          <td class="d-none d-sm-table-cell"><small>{{ formatDate(job.created_at) }}</small></td>
                          <td>
                            <button v-if="job.has_output && job.status === 'completed'"
                                    class="btn btn-sm btn-outline-success py-0 px-1"
                                    @click="downloadAudio(job.id)" title="Download processed audio">
                              <i class="fa fa-download"></i>
                            </button>
                            <span v-else class="text-muted small">Unavailable</span>
                          </td>
                        </tr>
                        <tr v-if="!recentAudioJobs.length">
                          <td colspan="9" class="text-muted">No audio jobs yet</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Recent Audio Failures -->
          <div class="row mb-4">
            <div class="col-12">
              <div class="card">
                <div class="card-header bg-danger text-white">Recent Audio Failures</div>
                <div class="card-body p-0 p-md-3">
                  <div class="table-responsive">
                    <table class="table table-sm mb-0">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>IP</th>
                          <th class="d-none d-md-table-cell">Profile</th>
                          <th class="d-none d-md-table-cell">Format</th>
                          <th>Error</th>
                          <th class="d-none d-sm-table-cell">Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="job in recentAudioFailures" :key="job.id">
                          <td><code class="small">{{ job.id }}</code></td>
                          <td><code class="small ip-cell">{{ job.ip }}</code></td>
                          <td class="d-none d-md-table-cell"><small>{{ job.profile }}</small></td>
                          <td class="d-none d-md-table-cell"><small>{{ job.format }}</small></td>
                          <td>
                            <small class="text-danger" style="word-break: break-word;">{{ job.error }}</small>
                          </td>
                          <td class="d-none d-sm-table-cell"><small>{{ formatDate(job.created_at) }}</small></td>
                        </tr>
                        <tr v-if="!recentAudioFailures.length">
                          <td colspan="6" class="text-muted">No audio failures</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Admin',
  data() {
    return {
      adminToken: '',
      authenticated: false,
      loading: false,
      error: null,
      metrics: null,
      apiBaseUrl: '',
      cancellingJob: null,
      announcements: [],
      showAnnouncementForm: false,
      newAnnouncement: {
        message: '',
        type: 'info'
      },
      newBlockedIp: ''
    }
  },
  computed: {
    usageStats() {
      if (this.metrics && this.metrics.usage_stats) {
        return this.metrics.usage_stats;
      }
      return {
        helper_clicks_24h: 0,
        helper_clicks_total: 0,
        ai_calls_24h: 0,
        ai_calls_total: 0,
        downloads_24h: 0,
        downloads_total: 0
      };
    },
    topUsers() {
      if (this.metrics && this.metrics.top_users) {
        return this.metrics.top_users;
      }
      return [];
    },
    topVisitors() {
      if (this.metrics && this.metrics.top_visitors) {
        return this.metrics.top_visitors;
      }
      return [];
    },
    topHelperUsers() {
      if (this.metrics && this.metrics.top_helper_users) {
        return this.metrics.top_helper_users;
      }
      return [];
    },
    blockedIps() {
      if (this.metrics && this.metrics.blocked_ips) {
        return this.metrics.blocked_ips;
      }
      return [];
    },
    recentModeration() {
      if (this.metrics && this.metrics.recent_moderation) {
        return this.metrics.recent_moderation;
      }
      return [];
    },
    audioOverview() {
      return this.metrics?.audio_overview || { total: 0, completed: 0, failed: 0, success_rate: 0 };
    },
    audioActivity() {
      return this.metrics?.audio_activity || { jobs_24h: 0, jobs_7d: 0, jobs_30d: 0 };
    },
    audioUsage() {
      return this.metrics?.audio_usage || {
        downloads_24h: 0, downloads_total: 0,
        previews_24h: 0, previews_total: 0
      };
    },
    recentAudioJobs() {
      return this.metrics?.recent_audio_jobs || [];
    },
    recentAudioFailures() {
      return this.metrics?.recent_audio_failures || [];
    },
    topAudioUsers() {
      return this.metrics?.top_audio_users || [];
    }
  },
  created() {
    this.apiBaseUrl = window.location.origin;
    
    const savedToken = localStorage.getItem('adminToken');
    if (savedToken) {
      this.adminToken = savedToken;
      this.login();
    }
  },
  methods: {
    async login() {
      this.loading = true;
      this.error = null;
      
      try {
        const response = await this.axios.get(`${this.apiBaseUrl}/api/admin/metrics`, {
          headers: { 'x-admin-token': this.adminToken }
        });
        
        if (response.data.success) {
          this.authenticated = true;
          this.metrics = response.data;
          localStorage.setItem('adminToken', this.adminToken);
          this.fetchAnnouncements();
        } else {
          this.error = 'Invalid response';
        }
      } catch (err) {
        if (err.response && err.response.status === 401) {
          this.error = 'Invalid admin token';
        } else if (err.response && err.response.status === 503) {
          this.error = 'Admin not configured. Set ADMIN_TOKEN environment variable.';
        } else {
          this.error = err.message || 'Connection failed';
        }
        localStorage.removeItem('adminToken');
      } finally {
        this.loading = false;
      }
    },
    
    async refreshMetrics() {
      this.loading = true;
      try {
        const response = await this.axios.get(`${this.apiBaseUrl}/api/admin/metrics`, {
          headers: { 'x-admin-token': this.adminToken }
        });
        if (response.data.success) {
          this.metrics = response.data;
        }
      } catch (err) {
        console.error('Refresh error:', err);
      } finally {
        this.loading = false;
      }
    },
    
    async clearStaleJobs() {
      if (!confirm('This will mark all stuck processing jobs (>45 min) as failed. Continue?')) {
        return;
      }
      try {
        const response = await this.axios.post(`${this.apiBaseUrl}/api/admin/clear-stale-jobs`, {}, {
          headers: { 'x-admin-token': this.adminToken }
        });
        if (response.data.success) {
          alert(response.data.message);
          this.refreshMetrics();
        }
      } catch (err) {
        alert('Error clearing stale jobs: ' + (err.response?.data?.error || err.message));
      }
    },
    
    async fetchAnnouncements() {
      try {
        const response = await this.axios.get(`${this.apiBaseUrl}/api/admin/announcements`, {
          headers: { 'x-admin-token': this.adminToken }
        });
        this.announcements = response.data.announcements || [];
      } catch (err) {
        console.error('Error fetching announcements:', err);
      }
    },
    
    async createAnnouncement() {
      if (!this.newAnnouncement.message.trim()) return;
      
      try {
        const response = await this.axios.post(`${this.apiBaseUrl}/api/admin/announcements`, {
          message: this.newAnnouncement.message,
          type: this.newAnnouncement.type
        }, {
          headers: { 'x-admin-token': this.adminToken }
        });
        
        if (response.data.success) {
          this.newAnnouncement.message = '';
          this.newAnnouncement.type = 'info';
          this.showAnnouncementForm = false;
          this.fetchAnnouncements();
        }
      } catch (err) {
        alert('Error creating announcement: ' + (err.response?.data?.error || err.message));
      }
    },
    
    async toggleAnnouncement(ann) {
      try {
        await this.axios.put(`${this.apiBaseUrl}/api/admin/announcements/${ann.id}`, {
          is_active: !ann.is_active
        }, {
          headers: { 'x-admin-token': this.adminToken }
        });
        this.fetchAnnouncements();
      } catch (err) {
        alert('Error updating announcement: ' + (err.response?.data?.error || err.message));
      }
    },
    
    async deleteAnnouncement(id) {
      if (!confirm('Delete this announcement?')) return;
      
      try {
        await this.axios.delete(`${this.apiBaseUrl}/api/admin/announcements/${id}`, {
          headers: { 'x-admin-token': this.adminToken }
        });
        this.fetchAnnouncements();
      } catch (err) {
        alert('Error deleting announcement: ' + (err.response?.data?.error || err.message));
      }
    },
    
    statusClass(status) {
      const classes = {
        completed: 'badge badge-success',
        failed: 'badge badge-danger',
        pending: 'badge badge-warning',
        processing: 'badge badge-info'
      };
      return classes[status] || 'badge badge-secondary';
    },
    
    formatDate(isoString) {
      if (!isoString) return '-';
      const date = new Date(isoString);
      return date.toLocaleString();
    },
    
    formatElapsed(seconds) {
      if (!seconds || seconds < 0) return '-';
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      if (mins >= 60) {
        const hrs = Math.floor(mins / 60);
        const remainingMins = mins % 60;
        return `${hrs}h ${remainingMins}m`;
      }
      return `${mins}m ${secs}s`;
    },
    
    shortenId(id) {
      if (!id) return '-';
      return id.substring(0, 8) + '...';
    },
    
    copyId(id) {
      navigator.clipboard.writeText(id).then(() => {
        alert('Full Job ID copied: ' + id);
      }).catch(() => {
        prompt('Copy this ID:', id);
      });
    },
    
    formatDuration(seconds) {
      if (!seconds && seconds !== 0) return '-';
      const mins = Math.floor(seconds / 60);
      const secs = Math.round(seconds % 60);
      return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
    },
    
    async cancelJob(jobId) {
      if (!confirm(`Cancel job ${jobId.substring(0, 8)}...?`)) return;
      
      this.cancellingJob = jobId;
      try {
        const response = await this.axios.post(`${this.apiBaseUrl}/api/admin/cancel-job/${jobId}`, {}, {
          headers: { 'x-admin-token': this.adminToken }
        });
        if (response.data.success) {
          await this.refreshMetrics();
        } else {
          alert('Cancel failed: ' + response.data.error);
        }
      } catch (err) {
        alert('Cancel failed: ' + (err.response?.data?.error || err.message));
      } finally {
        this.cancellingJob = null;
      }
    },
    
    async addBlockedIp() {
      const ip = this.newBlockedIp.trim();
      if (!ip) return;
      try {
        const response = await this.axios.post(`${this.apiBaseUrl}/api/admin/blocklist`, { ip }, {
          headers: { 'x-admin-token': this.adminToken }
        });
        if (response.data.success) {
          this.metrics.blocked_ips = response.data.blocked_ips;
          this.newBlockedIp = '';
        }
      } catch (err) {
        alert('Error blocking IP: ' + (err.response?.data?.error || err.message));
      }
    },

    async removeBlockedIp(ip) {
      if (!confirm(`Unblock IP ${ip}?`)) return;
      try {
        const response = await this.axios.delete(`${this.apiBaseUrl}/api/admin/blocklist/${encodeURIComponent(ip)}`, {
          headers: { 'x-admin-token': this.adminToken }
        });
        if (response.data.success) {
          this.metrics.blocked_ips = response.data.blocked_ips;
        }
      } catch (err) {
        alert('Error unblocking IP: ' + (err.response?.data?.error || err.message));
      }
    },

    blockIpFromModeration(ip) {
      this.newBlockedIp = ip;
      this.addBlockedIp();
    },

    async downloadAudio(jobId) {
      try {
        const response = await this.axios.get(`${this.apiBaseUrl}/api/admin/audio/download/${jobId}`, {
          headers: { 'x-admin-token': this.adminToken },
          responseType: 'blob'
        });
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'audio/wav' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = `processed_${jobId}.wav`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      } catch (err) {
        alert('Audio download failed. The file may no longer be available.');
      }
    },

    async downloadVideo(jobId) {
      try {
        const response = await this.axios.get(`${this.apiBaseUrl}/api/admin/download/${jobId}`, {
          headers: { 'x-admin-token': this.adminToken },
          responseType: 'blob'
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `quran_video_${jobId.substring(0, 8)}.mp4`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        alert('Download failed: ' + (err.response?.data?.error || err.message));
      }
    }
  }
}
</script>

<style scoped>
.admin-dashboard {
  min-height: 80vh;
  background: #f4f5f7;
  padding: 2rem 0;
  margin-top: -150px;
  position: relative;
  z-index: 10;
  border-radius: 8px 8px 0 0;
}

.card {
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  border: none;
  margin-bottom: 1rem;
}

.card-header {
  font-weight: 600;
  background: #f8f9fa;
}

.table-sm td, .table-sm th {
  padding: 0.4rem;
}

.table-fixed {
  table-layout: fixed;
  width: 100%;
}

.table-fixed td, .table-fixed th {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.text-truncate {
  max-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.badge-sm {
  font-size: 0.7rem;
  padding: 0.2em 0.5em;
}

code {
  font-size: 0.75rem;
  word-break: break-all;
}

.ip-cell {
  display: inline-block;
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}

@media (min-width: 768px) {
  .ip-cell {
    max-width: 150px;
  }
}

@media (min-width: 992px) {
  .ip-cell {
    max-width: none;
  }
}

.job-id-code {
  font-size: 0.85rem;
  user-select: all;
}
</style>
